import { useEffect, useState, type FormEvent } from "react";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Textarea } from "../ui/Textarea";
import type { BrandVoice as BrandVoiceType } from "../../../../shared/types";

interface BrandVoiceProps {
  voice: BrandVoiceType | null;
  onSave: (data: Partial<BrandVoiceType>) => void;
  saving?: boolean;
}

export function BrandVoice({ voice, onSave, saving }: BrandVoiceProps) {
  const [tone, setTone] = useState(voice?.tone || "");
  const [guidelines, setGuidelines] = useState(voice?.guidelines || "");
  const [toneInput, setToneInput] = useState("");
  const [toneDescriptors, setToneDescriptors] = useState<string[]>(voice?.tone_descriptors || []);
  const [vocabInput, setVocabInput] = useState("");
  const [vocabulary, setVocabulary] = useState<string[]>(voice?.vocabulary || []);
  const [postInput, setPostInput] = useState("");
  const [examplePosts, setExamplePosts] = useState<string[]>(voice?.example_posts || []);

  useEffect(() => {
    setTone(voice?.tone || "");
    setGuidelines(voice?.guidelines || "");
    setToneDescriptors(voice?.tone_descriptors || []);
    setVocabulary(voice?.vocabulary || []);
    setExamplePosts(voice?.example_posts || []);
    setToneInput("");
    setVocabInput("");
    setPostInput("");
  }, [voice]);

  function handleAddToneDescriptor() {
    if (toneInput.trim() && !toneDescriptors.includes(toneInput.trim())) {
      setToneDescriptors([...toneDescriptors, toneInput.trim()]);
      setToneInput("");
    }
  }

  function handleAddVocab() {
    if (vocabInput.trim() && !vocabulary.includes(vocabInput.trim())) {
      setVocabulary([...vocabulary, vocabInput.trim()]);
      setVocabInput("");
    }
  }

  function handleAddPost() {
    if (postInput.trim()) {
      setExamplePosts([...examplePosts, postInput.trim()]);
      setPostInput("");
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onSave({ tone, tone_descriptors: toneDescriptors, vocabulary, example_posts: examplePosts, guidelines });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Card className="space-y-4">
        <Input
          label="Tone description"
          value={tone}
          onChange={(e) => setTone(e.target.value)}
          placeholder="e.g. Professional but approachable, uses clear language"
        />
      </Card>

      <Card>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Tone descriptors</label>
        <div className="mb-2 flex flex-wrap gap-2">
          {toneDescriptors.map((descriptor) => (
            <span
              key={descriptor}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-3 py-1 text-sm text-brand-700"
            >
              {descriptor}
              <button
                type="button"
                onClick={() =>
                  setToneDescriptors(toneDescriptors.filter((entry) => entry !== descriptor))
                }
                aria-label={`Remove tone descriptor ${descriptor}`}
                className="ml-1 text-brand-500 hover:text-brand-700"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={toneInput}
            onChange={(e) => setToneInput(e.target.value)}
            placeholder="Add tone (e.g. confident, casual)"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddToneDescriptor();
              }
            }}
          />
          <Button type="button" variant="secondary" onClick={handleAddToneDescriptor}>
            Add
          </Button>
        </div>
      </Card>

      <Card>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">Vocabulary</label>
        <div className="mb-2 flex flex-wrap gap-2">
          {vocabulary.map((word) => (
            <span
              key={word}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-sm text-slate-700"
            >
              {word}
              <button
                type="button"
                onClick={() => setVocabulary(vocabulary.filter((w) => w !== word))}
                aria-label={`Remove vocabulary term ${word}`}
                className="ml-1 text-slate-400 hover:text-slate-600"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            value={vocabInput}
            onChange={(e) => setVocabInput(e.target.value)}
            placeholder="Key term or jargon"
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); handleAddVocab(); }
            }}
          />
          <Button type="button" variant="secondary" onClick={handleAddVocab}>Add</Button>
        </div>
      </Card>

      <Card>
        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          Example posts ({examplePosts.length})
        </label>
        {examplePosts.map((post, i) => (
          <div key={i} className="mb-2 flex items-start gap-2 rounded-lg border border-slate-200 p-3">
            <p className="flex-1 text-sm text-slate-600">{post}</p>
            <button
              type="button"
              onClick={() => setExamplePosts(examplePosts.filter((_, j) => j !== i))}
              aria-label={`Remove example post ${i + 1}`}
              className="text-slate-400 hover:text-red-500"
            >
              &times;
            </button>
          </div>
        ))}
        <Textarea
          value={postInput}
          onChange={(e) => setPostInput(e.target.value)}
          placeholder="Paste an example post in your brand voice..."
          rows={3}
        />
        <Button type="button" variant="secondary" onClick={handleAddPost} className="mt-2">
          Add example
        </Button>
      </Card>

      <Card className="space-y-4">
        <Textarea
          label="Voice guidelines"
          value={guidelines}
          onChange={(e) => setGuidelines(e.target.value)}
          placeholder="Any special writing guidelines, do's and don'ts..."
          rows={3}
        />
      </Card>

      <Button type="submit" disabled={saving}>
        {saving ? "Saving..." : "Save voice settings"}
      </Button>
    </form>
  );
}
